import { SyntheticEvent, useState } from 'react';
import { Box, Accordion, AccordionDetails, AccordionSummary, IconButton, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

import { RooftopCreateDto, RooftopUpdateDto } from '@libs/dto';
import { Rooftop } from '@libs/models';
import { RooftopInput } from '@carcareers/components';
import { useForm } from '@carcareers/contexts';

export interface RooftopsInputProps {
  type: 'authorizedRooftops' | 'ownedRooftops' | 'rooftops';
}

type RooftopInputDtos = RooftopCreateDto | RooftopUpdateDto | RooftopCreateDto[] | RooftopUpdateDto[] | undefined;

export const RooftopsInput = <T extends RooftopInputDtos>({ type }: RooftopsInputProps) => {
  const [expandedPanel, setExpandedPanel] = useState<number>(-1);
  const { getData, setData, getErrors } = useForm<T>();

  const getTitle = () => {
    switch (type) {
      case 'authorizedRooftops':
        return 'Authorized Rooftops';
      case 'ownedRooftops':
        return 'Owned Rooftops';
      case 'rooftops':
      default:
        return 'Rooftops';
    }
  };

  const handleChange = (index: number) => (_: SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? index : -1);
  };

  const handleDelete = (index: number) => {
    setExpandedPanel(-1);
    const data = getData<Rooftop[]>(type);
    if (data) {
      setData(type, data?.filter((_, i) => i !== index) ?? []);
    }
  };

  const handleAddRooftop = () => {
    const data = getData(type);
    setExpandedPanel(data?.length ?? 0);
    if (data) {
      setData(type, [...(data as []), {}]);
    } else {
      setData(type, [{}]);
    }
  };

  const rooftops = getData(type) || [];
  const errors = getErrors(type);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">{getTitle()}</Typography>
        <Tooltip title="Add Rooftop">
          <IconButton color="secondary" onClick={handleAddRooftop} sx={{ width: 'fit-content' }}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {errors !== '' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {errors}
          </Typography>
        </Box>
      )}
      <Box>
        {rooftops.map((rooftop: Rooftop, index: number) => (
          <Accordion 
            key={`${type}-${index}`} 
            expanded={expandedPanel === index} 
            onChange={handleChange(index)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              <Tooltip title="Remove rooftop">
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(index);
                  }}
                  sx={{ mr: 2, p: 1, borderRadius: '50%', '&:hover': { backgroundColor: 'grey.100' } }}
                >
                  <CloseIcon sx={{ color: 'grey.700', width: '21px', height: '21px' }} />
                </Box>
              </Tooltip>
              <Typography variant="subtitle2" sx={{ height: 'fit-content' }}>
                {rooftop.name || `New Rooftop ${index + 1}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <RooftopInput type={type} index={index} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};
